import { collection, doc, onSnapshot, updateDoc } from "firebase/firestore";
import { useEffect } from "react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../Firebase";
import ControlDashboard from "./ControlDashboard";
import GameSession from "./GameSession";

const GameController = ({ isAdmin }) => {
  let { sessionId } = useParams();
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState({});
  const [sentence, setSentence] = useState("");
  const [words, setWords] = useState([]);
  const [showBoxes, setShowBoxes] = useState([]);
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [songUri, setSongUri] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);

  useEffect(() => {
    document.title = "Beat 4 beat";
  }, []);

  useEffect(() => {
    if (sessionData.currentIndex !== null && sessionData.currentIndex > -1) {
      updateItemLocally();
    }
  }, [sessionData.currentIndex]);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "sessions", sessionId), (doc) => {
      let data = doc.data();
      if (data.songList) {
        setSessionData(data);
      }
    });

    return () => {
      unsub();
    };
  }, [sessionId]);

  // Get total number of game items (uses gameItems if available, falls back to songs)
  const getTotalItems = () => {
    if (sessionData.gameItems?.length > 0) {
      return sessionData.gameItems.length;
    }
    return sessionData.songList?.songs?.length || 0;
  };

  // Get item at a specific index (from gameItems or fallback to songs)
  const getItemAtIndex = (index) => {
    if (sessionData.gameItems?.length > 0) {
      return sessionData.gameItems[index];
    }
    // Fallback for sessions without gameItems (backwards compatibility)
    const song = sessionData.songList?.songs?.[index];
    return song ? { type: "song", data: song } : null;
  };

  const updateSong = async () => {
    if (sessionData.currentIndex < 0) {
      return;
    }
    const nextIndex = sessionData.currentIndex + 1;
    //Update song in db: index and playedSongs
    if (!sessionData.gameStarted) {
      updateSessionData({ gameStarted: true, showAnswer: false });
    }
    if (nextIndex > getTotalItems() - 1) {
      alert("Tomt for sanger :/");
    } else {
      await updateIndex(nextIndex);
      applyItem(nextIndex);
    }
  };

  // Apply the current game item (song or question) to local state
  const applyItem = (index) => {
    const item = getItemAtIndex(index);
    if (!item) return;

    if (item.type === "question") {
      // It's a question - clear song state and set question
      setSentence("");
      setTitle("");
      setArtist("");
      setSongUri(null);
      setCurrentQuestion(item.data);
    } else {
      // It's a song
      const songData = item.data || item;
      setSentence(songData.text || "");
      setTitle(songData.title || "");
      setArtist(songData.artist || "");
      setSongUri(songData.uri || null);
      setCurrentQuestion(null);
    }
  };

  const updateItemLocally = () => {
    applyItem(sessionData.currentIndex);
  };

  useEffect(() => {
    if (!sessionData.songList) {
      return;
    }
    const totalItems = getTotalItems();
    if (
      totalItems > 0 &&
      !sessionData.gameStarted &&
      sessionData.currentIndex === -1
    ) {
      updateSong();
    } else {
    }
  }, [sessionData.songList, sessionData.currentIndex, sessionData.gameStarted]);

  useEffect(() => {
    // Don't process words/red numbers for questions (empty sentence)
    if (!sentence) {
      setWords([]);
      return;
    }
    const wordArray = sentence.split(" ");
    setWords(wordArray);
    //Two random numbers need to be red
    const possibleNumbers = [0, 1, 2, 3, 4, 5];
    const randomNumberOne = possibleNumbers.splice(
      Math.floor(Math.random() * possibleNumbers.length),
      1
    );
    const randomNumberTwo = possibleNumbers.splice(
      Math.floor(Math.random() * possibleNumbers.length),
      1
    );
    //setRedNumbers([randomNumberOne[0], randomNumberTwo[0]]);
    updateSessionData({
      showAnswer: false,
      redNumbers: [randomNumberOne[0], randomNumberTwo[0]],
    });
  }, [sentence]);

  const endGame = () => {
    //Clear everything
    // setsessionData.gameStarted(false);
    // setsessionData.currentIndex(-1);
    // setHasFinishedSetup(false);
    updateSessionData({
      gameStarted: false,
      currentIndex: -1,
      showAnswer: false,
      showBoxes: [],
    });
    navigate("/setup");
  };

  const updateShowBoxes = async (boxIndex) => {
    try {
      let newShowBoxes = [...sessionData.showBoxes];
      if (newShowBoxes?.includes(boxIndex)) {
        //Remove from array
        newShowBoxes = newShowBoxes.filter((i) => i !== boxIndex);
      } else {
        newShowBoxes.push(boxIndex);
      }
      const docRef = await doc(collection(db, "sessions"), sessionId);
      await updateDoc(docRef, {
        showBoxes: newShowBoxes,
      });
    } catch (e) {
      console.error("Error adding document: ", e);
    }
  };

  const updateIndex = async (newIndex) => {
    try {
      const docRef = await doc(collection(db, "sessions"), sessionId);
      await updateDoc(docRef, {
        currentIndex: newIndex,
        showAnswer: false,
        showBoxes: [],
        gameStarted: true,
        showQuestion: false,
        showQuestionCountdown: false,
      });
    } catch (e) {
      console.error("Error adding document: ", e);
    }
  };

  const updateMaxScore = async (newMaxScore) => {
    if (
      newMaxScore > sessionData.team1.score ||
      newMaxScore > sessionData.team2.score
    ) {
      return;
    }
    //Update maxscore in db
    try {
      const docRef = await doc(collection(db, "sessions"), sessionId);
      await updateDoc(docRef, {
        maxScore: newMaxScore,
      });
    } catch (e) {
      console.error("Error adding document: ", e);
    }
  };

  //newValues is an object with key value pairs
  const updateSessionData = async (newValues) => {
    //Update maxscore in db
    try {
      const docRef = await doc(collection(db, "sessions"), sessionId);
      await updateDoc(docRef, newValues);
    } catch (e) {
      console.error("Error adding document: ", e);
    }
  };

  return isAdmin ? (
    <ControlDashboard
      sessionData={sessionData}
      endGame={endGame}
      updateMaxScore={updateMaxScore}
      words={words}
      title={title}
      artist={artist}
      updateSessionData={updateSessionData}
      updateSong={updateSong}
      updateShowBoxes={updateShowBoxes}
      currentQuestion={currentQuestion}
    />
  ) : (
    <GameSession
      sessionData={sessionData}
      endGame={endGame}
      updateMaxScore={updateMaxScore}
      words={words}
      title={title}
      artist={artist}
      updateSessionData={updateSessionData}
      updateSong={updateSong}
      updateShowBoxes={updateShowBoxes}
      currentQuestion={currentQuestion}
    />
  );
};

export default GameController;
